import { WordSearchPuzzle, Cell } from '@/wordsearch/types';

function areInStraightLine(cellA: Cell, cellB: Cell): boolean {
  return (
    (cellA.x === cellB.x)
    || (cellA.y === cellB.y)
    || (Math.abs(cellB.x - cellA.x) === Math.abs(cellB.y - cellA.y))
  );
}

function getCellByXY(data: WordSearchPuzzle, x: number, y: number): Cell | null {
  return data.cells.find((cell) => cell.x === x && cell.y === y) || null;
}

function mapCellIdToCell(data: WordSearchPuzzle, cellId: string): Cell {
  return data.cells.find((cell) => cell.id === cellId)!;
}

export function mapCellsIdsToCells(data: WordSearchPuzzle, cellsIds: string[]): Cell[] {
  return cellsIds.map((id) => mapCellIdToCell(data, id));
}

function getCellsOnLine(
  data: WordSearchPuzzle,
  start: Cell,
  end: Cell,
): Cell[] {
  const diffX = Math.sign(end.x - start.x);
  const diffY = Math.sign(end.y - start.y);

  const result: Cell[] = [start];

  let currentCell = start;

  while (currentCell !== end) {
    const nextCell = getCellByXY(
      data, currentCell.x + diffX, currentCell.y + diffY,
    );

    if (!nextCell) return [];
    result.push(nextCell);
    currentCell = nextCell;
  }

  return result;
}

function straightenLineEnd(data: WordSearchPuzzle, start: Cell, end: Cell): Cell | null {
  const xDiff = end.x - start.x;
  const yDiff = end.y - start.y;

  if (Math.abs(xDiff) < Math.abs(yDiff)) {
    const y = start.y + Math.sign(yDiff) * Math.abs(xDiff);
    return getCellByXY(data, end.x, y);
  }
  const x = start.x + Math.sign(xDiff) * Math.abs(yDiff);
  return getCellByXY(data, x, end.y);
}

export function getWordOutOfCells(cells: Cell[]): string {
  return cells.map((cell) => cell.letter).join('');
}

export function getSelection(
  data: WordSearchPuzzle,
  start: Cell,
  end: Cell,
): Cell[] {
  if (areInStraightLine(start, end)) {
    return getCellsOnLine(data, start, end);
  }
  const newEnd = straightenLineEnd(data, start, end);
  if (!newEnd) return [];
  return getCellsOnLine(data, start, newEnd);
}

export function isRightAnswer(data: WordSearchPuzzle, start: Cell, end: Cell): boolean {
  return data.words.includes(getWordOutOfCells(getSelection(data, start, end)));
}
